import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Pill, Check, X, Plus, Edit2, RotateCcw, AlertCircle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import {
  medicationsService, PatientMedication, MedicationSource,
} from '@/services/medicationsService';
import { parseApiError } from '@/lib/apiErrors';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';

interface ReconcileMedicationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  patientId: string;
  /** When provided, a reconciliation event is recorded against this encounter. */
  encounterId?: string;
  onCompleted?: () => void;
}

/** Per-row UI state — what the practitioner wants to do with this med. */
type RowDecision = 'pending' | 'continue' | 'stop' | 'modify';

interface RowState {
  decision: RowDecision;
  // Used while "modify" — staged changes not yet sent.
  draftDose?: string;
  draftRoute?: string;
}

interface AddDraft {
  // Stable client-side ID so partial-save retries skip already-created rows
  // even if the user mutates the pending list between attempts.
  id: string;
  display: string;
  doseText: string;
  route: string;
  source: MedicationSource;
  note: string;
}

const emptyAddDraft = (): AddDraft => ({
  id: globalThis.crypto.randomUUID(),
  display: '',
  doseText: '',
  route: '',
  source: 'patient_reported',
  note: '',
});

/**
 * Medication reconciliation — proper add / modify / continue / stop flow.
 *
 * Each active row carries a decision: continue (default), modify, or stop.
 * A separate "Add" affordance lets the practitioner record meds the patient
 * just disclosed during the visit. A "no active medications" toggle is
 * available when the chart is empty so the practitioner can affirmatively
 * record that fact.
 *
 * "Done" submits the changes in one batch: each "stop" calls
 * `stopMedication`, each "modify" calls `updateMedication`, then a single
 * `recordReconciliation` event captures the counts and notes.
 */
const ReconcileMedicationsDialog: React.FC<ReconcileMedicationsDialogProps> = ({
  open, onOpenChange, clinicId, patientId, encounterId, onCompleted,
}) => {
  const qc = useQueryClient();
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [adds, setAdds] = useState<AddDraft[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draft, setDraft] = useState<AddDraft>(emptyAddDraft());
  const [noActiveMeds, setNoActiveMeds] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: meds = [], isLoading } = useQuery({
    queryKey: cacheKeys.patientMedications(patientId, { activeOnly: true }),
    queryFn: () => medicationsService.getPatientMedications(clinicId, patientId, { activeOnly: true }),
    enabled: open,
  });

  const latestReconciliationKey = useMemo(
    () => ['medication-reconciliation-latest', clinicId, encounterId] as const,
    [clinicId, encounterId],
  );
  // Gate on `isFetching` (not `isFetched`). After save→invalidate→reopen,
  // `isFetched` is still true from the prior fetch — react-query keeps the
  // stale cached value while the background refetch is in flight. Waiting
  // for `isFetching` to flip false guarantees we hydrate with post-save data.
  const { data: latestReconciliation, isFetching: isLatestFetching } = useQuery({
    queryKey: latestReconciliationKey,
    queryFn: () => medicationsService.getLatestReconciliation(clinicId, encounterId!),
    enabled: open && !!encounterId,
  });

  // Refs survive across re-renders within a single open session. They reset
  // when the dialog closes. Used to (a) hydrate notes/flag exactly once per
  // open, so the user's typing isn't clobbered by a late query resolve, and
  // (b) make `finish()` idempotent on retry — already-applied stops, mods,
  // adds, and the recon-event POST are skipped if a later step failed.
  const hasHydratedRef = useRef(false);
  const completedDecisionsRef = useRef<Set<string>>(new Set());
  const completedAddsRef = useRef<Set<string>>(new Set());
  const reconciliationRecordedRef = useRef(false);

  // Reset transient state when dialog opens fresh.
  useEffect(() => {
    if (open) {
      setRowStates({});
      setAdds([]);
      setShowAddForm(false);
      setDraft(emptyAddDraft());
      hasHydratedRef.current = false;
      completedDecisionsRef.current = new Set();
      completedAddsRef.current = new Set();
      reconciliationRecordedRef.current = false;
    }
  }, [open]);

  // Hydrate notes + no-active-meds flag exactly once per open, after the
  // latest-event query has settled. Skipping post-hydration changes prevents
  // overwriting input the user has already started typing.
  useEffect(() => {
    if (!open || hasHydratedRef.current) return;
    if (encounterId && isLatestFetching) return;
    hasHydratedRef.current = true;
    setNotes(latestReconciliation?.notes ?? '');
    setNoActiveMeds(latestReconciliation?.no_active_medications ?? false);
  }, [open, encounterId, isLatestFetching, latestReconciliation]);

  const setDecision = (id: string, decision: RowDecision, init?: Partial<RowState>) => {
    setRowStates(s => ({
      ...s,
      [id]: {
        decision,
        draftDose: init?.draftDose ?? s[id]?.draftDose,
        draftRoute: init?.draftRoute ?? s[id]?.draftRoute,
      },
    }));
  };

  const addPendingMed = () => {
    if (!draft.display.trim()) {
      toast({ title: 'Add a medication name', variant: 'destructive' });
      return;
    }
    setAdds(a => [...a, { ...draft }]);
    setDraft(emptyAddDraft());
    setShowAddForm(false);
  };

  const removePendingAdd = (id: string) => {
    setAdds(a => a.filter(x => x.id !== id));
  };

  const counts = useMemo(() => {
    const stopped = Object.values(rowStates).filter(r => r.decision === 'stop').length;
    const modified = Object.values(rowStates).filter(r => r.decision === 'modify').length;
    const added = adds.length;
    const unchanged = meds.length - stopped - modified;
    return { stopped, modified, added, unchanged, reviewed: meds.length };
  }, [rowStates, adds, meds.length]);

  const finish = async () => {
    setSaving(true);
    try {
      // Apply per-row changes first so the server sees the right counts in
      // the recon event. Failures abort — the user retries Done. Each
      // successful step is recorded in a ref so a retry skips the
      // already-applied work instead of hitting (e.g.) a stopped med again.
      for (const m of meds) {
        const s = rowStates[m.id];
        if (!s || s.decision === 'pending' || s.decision === 'continue') continue;
        if (completedDecisionsRef.current.has(m.id)) continue;
        if (s.decision === 'stop') {
          await medicationsService.stopMedication(clinicId, m.id, {
            note: 'Stopped during reconciliation',
          });
        } else if (s.decision === 'modify') {
          await medicationsService.updateMedication(clinicId, m.id, {
            doseText: (s.draftDose ?? m.dose_text ?? '').trim() || undefined,
            route: (s.draftRoute ?? m.route ?? '').trim() || undefined,
          });
        }
        completedDecisionsRef.current.add(m.id);
      }

      for (const a of adds) {
        if (completedAddsRef.current.has(a.id)) continue;
        await medicationsService.createMedication(clinicId, patientId, {
          medicationDisplay: a.display.trim(),
          medicationCodeSystem: 'free-text',
          doseText: a.doseText.trim() || undefined,
          route: a.route.trim() || undefined,
          status: 'active',
          source: a.source,
          note: a.note.trim() || undefined,
          encounterId,
        });
        completedAddsRef.current.add(a.id);
      }

      // Record the reconciliation event (also stamps last_reconciled_at on
      // the patient's active meds server-side). Guarded so a retry after a
      // post-event failure doesn't duplicate the event row.
      if (encounterId && !reconciliationRecordedRef.current) {
        await medicationsService.recordReconciliation(clinicId, encounterId, {
          reviewedCount: counts.reviewed,
          addedCount: counts.added,
          stoppedCount: counts.stopped,
          unchangedCount: Math.max(0, counts.unchanged),
          noActiveMedications: noActiveMeds && meds.length === 0 && adds.length === 0,
          notes: notes.trim() || undefined,
        });
        reconciliationRecordedRef.current = true;
      }

      qc.invalidateQueries({ queryKey: cacheRoots.patientMedications(patientId) });
      qc.invalidateQueries({ queryKey: latestReconciliationKey });
      toast({ title: 'Medications reconciled', variant: 'success' });
      onCompleted?.();
      onOpenChange(false);
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Reconciliation failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = counts.stopped > 0 || counts.modified > 0 || counts.added > 0
    || noActiveMeds || notes.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Pill className='h-4 w-4' /> Reconcile medications
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {/* Summary chips — show what will be applied on Done */}
          <div className='flex flex-wrap gap-1.5 text-xs'>
            <Badge variant='outline'>Reviewed {counts.reviewed}</Badge>
            {counts.added > 0 && (
              <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>
                +{counts.added} new
              </Badge>
            )}
            {counts.modified > 0 && (
              <Badge className='bg-blue-100 text-blue-700 hover:bg-blue-100'>
                {counts.modified} modified
              </Badge>
            )}
            {counts.stopped > 0 && (
              <Badge className='bg-rose-100 text-rose-700 hover:bg-rose-100'>
                {counts.stopped} stopped
              </Badge>
            )}
          </div>

          {/* Active list */}
          {isLoading ? (
            <div className='flex justify-center py-4'>
              <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
            </div>
          ) : meds.length === 0 ? (
            <div className='rounded border border-dashed border-slate-200 p-3 text-sm text-slate-500'>
              No active medications on file. Add any meds the patient is currently taking, or check
              "no active medications" below to record that fact.
            </div>
          ) : (
            <ul className='divide-y divide-slate-100 rounded border border-slate-200'>
              {meds.map(m => (
                <MedRow
                  key={m.id}
                  med={m}
                  state={rowStates[m.id]}
                  onSetDecision={setDecision}
                />
              ))}
            </ul>
          )}

          {/* Add new — pending list + form */}
          {adds.length > 0 && (
            <div className='space-y-1'>
              <div className='text-xs font-semibold uppercase tracking-wide text-emerald-700'>
                To be added
              </div>
              <ul className='divide-y divide-slate-100 rounded border border-emerald-200 bg-emerald-50/30'>
                {adds.map(a => (
                  <li key={a.id} className='flex items-center justify-between gap-2 px-3 py-2 text-sm'>
                    <div className='min-w-0 flex-1'>
                      <div className='font-medium'>{a.display}</div>
                      <div className='text-xs text-slate-500'>
                        {[a.doseText, a.route, SOURCE_LABEL[a.source]].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <Button size='sm' variant='ghost' onClick={() => removePendingAdd(a.id)}>
                      <X className='h-3 w-3' />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {showAddForm ? (
            <div className='rounded border border-dashed border-slate-300 p-3 space-y-2'>
              <div className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
                Add medication
              </div>
              <div className='grid gap-2 sm:grid-cols-2'>
                <div className='sm:col-span-2'>
                  <Label htmlFor='add-display' className='text-xs'>Medication name</Label>
                  <Input
                    id='add-display'
                    autoFocus
                    placeholder='e.g. Metformin 500 mg'
                    value={draft.display}
                    onChange={e => setDraft(d => ({ ...d, display: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor='add-dose' className='text-xs'>Dose (sig)</Label>
                  <Input
                    id='add-dose'
                    placeholder='1 tab BID'
                    value={draft.doseText}
                    onChange={e => setDraft(d => ({ ...d, doseText: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor='add-route' className='text-xs'>Route</Label>
                  <Input
                    id='add-route'
                    placeholder='Oral'
                    value={draft.route}
                    onChange={e => setDraft(d => ({ ...d, route: e.target.value }))}
                  />
                </div>
                <div className='sm:col-span-2'>
                  <Label className='text-xs'>Source</Label>
                  <Select
                    value={draft.source}
                    onValueChange={(v) => setDraft(d => ({ ...d, source: v as MedicationSource }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SOURCE_LABEL).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='add-note' className='text-xs'>Note (optional)</Label>
                  <Input
                    id='add-note'
                    value={draft.note}
                    onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
                  />
                </div>
              </div>
              <div className='flex gap-2'>
                <Button size='sm' onClick={addPendingMed}>
                  <Plus className='mr-1 h-3 w-3' /> Add to list
                </Button>
                <Button size='sm' variant='ghost' onClick={() => { setShowAddForm(false); setDraft(emptyAddDraft()); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant='outline' size='sm' onClick={() => setShowAddForm(true)}>
              <Plus className='mr-1 h-3 w-3' /> Add medication
            </Button>
          )}

          {/* No active meds toggle (only meaningful when list is empty) */}
          {meds.length === 0 && (
            <label className='flex items-center gap-2 text-sm'>
              <Checkbox
                checked={noActiveMeds}
                onCheckedChange={(v) => setNoActiveMeds(v === true)}
              />
              Patient reports no active medications
            </label>
          )}

          {/* Notes */}
          {encounterId && (
            <div className='space-y-1'>
              <Label htmlFor='rec-notes' className='text-xs'>Reconciliation notes (optional)</Label>
              <Textarea
                id='rec-notes'
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder='Source: patient verbal report. Pending pharmacy callback for…'
                className='min-h-[60px]'
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={finish} disabled={saving}>
            {saving
              ? <><Loader2 className='mr-2 h-4 w-4 animate-spin' /> Saving…</>
              : !hasChanges && meds.length > 0
                ? <><Check className='mr-2 h-4 w-4' /> Confirm unchanged</>
                : <><Check className='mr-2 h-4 w-4' /> Done</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SOURCE_LABEL: Record<MedicationSource, string> = {
  patient_reported: 'Patient reported',
  rx_this_clinic: 'Rx (this clinic)',
  rx_external: 'Rx (outside)',
  imported_ccda: 'Imported (CCDA)',
  pharmacy_fill: 'Pharmacy fill',
};

const MedRow: React.FC<{
  med: PatientMedication;
  state?: RowState;
  onSetDecision: (id: string, decision: RowDecision, init?: Partial<RowState>) => void;
}> = ({ med, state, onSetDecision }) => {
  const decision = state?.decision || 'pending';
  return (
    <li className='px-3 py-2 text-sm'>
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>
          <div className={`font-medium ${decision === 'stop' ? 'line-through text-slate-500' : ''}`}>
            {med.medication_display}
          </div>
          <div className='mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500'>
            {med.dose_text && <span>{med.dose_text}</span>}
            {med.route && <span>· {med.route}</span>}
            <Badge variant='outline' className='text-[10px]'>{med.source.replace(/_/g, ' ')}</Badge>
          </div>
        </div>
        <RowDecisionBadge decision={decision} />
      </div>

      {decision === 'modify' && (
        <div className='mt-2 grid gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-2'>
          <div>
            <Label className='text-[10px] uppercase'>Dose / sig</Label>
            <Input
              defaultValue={med.dose_text || ''}
              onChange={e => onSetDecision(med.id, 'modify', { draftDose: e.target.value })}
            />
          </div>
          <div>
            <Label className='text-[10px] uppercase'>Route</Label>
            <Input
              defaultValue={med.route || ''}
              onChange={e => onSetDecision(med.id, 'modify', { draftRoute: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className='mt-2 flex flex-wrap gap-1.5'>
        <Button
          size='sm'
          variant={decision === 'continue' ? 'default' : 'outline'}
          onClick={() => onSetDecision(med.id, 'continue')}
          className='h-7 text-xs'
        >
          <Check className='mr-1 h-3 w-3' /> Continue
        </Button>
        <Button
          size='sm'
          variant={decision === 'modify' ? 'default' : 'outline'}
          onClick={() => onSetDecision(med.id, 'modify', { draftDose: med.dose_text || '', draftRoute: med.route || '' })}
          className='h-7 text-xs'
        >
          <Edit2 className='mr-1 h-3 w-3' /> Modify
        </Button>
        <Button
          size='sm'
          variant={decision === 'stop' ? 'default' : 'outline'}
          onClick={() => onSetDecision(med.id, 'stop')}
          className={`h-7 text-xs ${decision === 'stop' ? 'bg-rose-600 hover:bg-rose-700' : ''}`}
        >
          <X className='mr-1 h-3 w-3' /> Stop
        </Button>
        {decision !== 'pending' && (
          <Button
            size='sm'
            variant='ghost'
            onClick={() => onSetDecision(med.id, 'pending')}
            className='h-7 text-xs text-slate-500'
          >
            <RotateCcw className='mr-1 h-3 w-3' /> Reset
          </Button>
        )}
      </div>
    </li>
  );
};

const RowDecisionBadge: React.FC<{ decision: RowDecision }> = ({ decision }) => {
  if (decision === 'continue') {
    return <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>Continue</Badge>;
  }
  if (decision === 'modify') {
    return <Badge className='bg-blue-100 text-blue-700 hover:bg-blue-100'>Modify</Badge>;
  }
  if (decision === 'stop') {
    return <Badge className='bg-rose-100 text-rose-700 hover:bg-rose-100'>Stop</Badge>;
  }
  return <span className='inline-flex items-center gap-1 text-[10px] text-slate-400'>
    <AlertCircle className='h-3 w-3' /> Decide
  </span>;
};

export default ReconcileMedicationsDialog;
